import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { statusHelper } from 'src/app/helpers';
import { userActions } from 'src/app/redux/actions';
import { userService } from 'src/app/services';
import { reduxRootTypes } from 'src/types';
import DraggableModal from '../../DraggableModal/DraggableModal';
import { passwordChangeProps, UpdatePasswordFormType } from './passwordChange.config';
import styles from './passwordChange.module.scss';

import { errorLocals } from 'src/locals';
import useLogout from 'src/app/hooks/useLogout';
import { useTranslation } from 'react-i18next';

import Input from 'antd/es/input';
import Form from 'antd/es/form';

const PasswordChangeForm: React.FC<passwordChangeProps> = ({ isModalOpen, setIsModalOpen, setOnClickProfileOpen }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const dispatch = useDispatch();
  const logout = useLogout();

  const onFormSave = async (formData: UpdatePasswordFormType) => {
    try {
      const userUpdateData = await userService.UpdateMePassword({
        ...formData,
      });

      dispatch(userActions.setInformation(userUpdateData.data));
      statusHelper.displaySuccessStatusMessage(t('success.updated'));

      setIsModalOpen(false);
      setOnClickProfileOpen && setOnClickProfileOpen(false);
      form.resetFields();
    } catch (error) {
      statusHelper.displayGenericError(error);
    }
  };

  const onNext = async () => {
    if (userState.isRegistered) {
      setIsModalOpen(false);
    } else {
      try {
        const userUpdateData = await userService.updateMeProfile({
          id: userState.id,
          isRegistered: true,
        });

        dispatch(userActions.setInformation(userUpdateData.data));
        setIsModalOpen(false);
      } catch (error: any) {
        if (error !== errorLocals.errorHandledError) {
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    }
  };

  const onCloseModal = () => {
    if (!userState.isRegistered) {
      logout();
    }
    setIsModalOpen(false);
  };

  return (
    <>
      {isModalOpen && (
        <DraggableModal
          headerText={t('administration.users.changePassword')}
          onClickClose={onCloseModal}
          style={{ height: '300px' }}
        >
          <Form
            onFinish={onFormSave}
            form={form}
            initialValues={{
              oldPassword: '',
              password: '',
              passwordConfirmation: '',
            }}
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className={styles.container}>
                  <div className="popup-input-container">
                    <span className="popup-input-title">{t('administration.users.oldPassword')}</span>
                    <Form.Item
                      name="oldPassword"
                      rules={[
                        {
                          required: true,
                          message: t('general.required') || '',
                        },
                      ]}
                    >
                      <Input.Password
                        type="password"
                        className={styles.standard_input}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </Form.Item>
                  </div>
                  <div className="popup-row grid-col-2-fixed">
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.users.newPassword')}</span>
                      <Form.Item
                        name="password"
                        dependencies={['passwordConfirmation']}
                        rules={[
                          {
                            required: true,
                            message: t('general.required') || '',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const pass = getFieldValue('passwordConfirmation');
                              if (pass === value || (!value && !pass)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('The two passwords do not match!'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password
                          type="password"
                          className={styles.standard_input}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.users.passwordConfirmation')}</span>
                      <Form.Item
                        name="passwordConfirmation"
                        rules={[
                          {
                            required: true,
                            message: t('general.required') || '',
                          },
                        ]}
                      >
                        <Input.Password
                          type="password"
                          className={styles.standard_input}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="popup-footer" style={{ marginRight: '6%' }}>
              <div>
                <button className="next-button" type="submit">
                  {t('general.save')}
                </button>
                <button className="modal-button" onClick={onNext} type="button">
                  {userState.isRegistered ? t('general.cancel') : t('general.cancel')}
                </button>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}
    </>
  );
};

export default PasswordChangeForm;
